<script setup lang="ts">
/**
 * 对应 React 版 app/layout.tsx 里的 `<Toaster />`（来自 react-hot-toast）。
 *
 * 外观按 react-hot-toast 的默认样式还原（白底、8px 圆角、轻阴影）。
 * 调用方可以通过 `className` / `style` 覆盖 —— 例如 RecordingIndicator 会把底色换成
 * `var(--lk-danger3)`，那条路径必须与原实现视觉一致。
 */
import { toasts } from '@/lib/toast';
</script>

<template>
  <div class="lk-toast-host">
    <div
      v-for="t in toasts"
      :key="t.id"
      class="lk-toast"
      :class="[t.className, `lk-toast--${t.position ?? 'top-center'}`]"
      :style="t.style"
      role="status"
      aria-live="polite"
    >
      <span v-if="t.icon" class="lk-toast-icon" aria-hidden="true">{{ t.icon }}</span>
      <span class="lk-toast-message">{{ t.message }}</span>
    </div>
  </div>
</template>

<style scoped>
/* 容器本身不吃点击，避免挡住下层会议的按钮 */
.lk-toast-host {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.lk-toast {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: min(420px, calc(100vw - 2rem));
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background-color: #fff;
  color: #363636;
  font-size: 0.875rem;
  line-height: 1.3;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1), 0 3px 3px rgba(0, 0, 0, 0.05);
  pointer-events: auto;
  animation: lk-toast-in 150ms ease-out;
}

.lk-toast--top-center {
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
}

.lk-toast--top-right {
  top: 1rem;
  right: 1rem;
}

.lk-toast--bottom-center {
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
}

.lk-toast--bottom-right {
  bottom: 1rem;
  right: 1rem;
}

.lk-toast-icon {
  flex-shrink: 0;
}

@keyframes lk-toast-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 右上/右下的定位不带 translateX，单独给一组动画，避免动画把位置带歪 */
.lk-toast--top-right,
.lk-toast--bottom-right {
  transform: none;
  animation-name: lk-toast-in-right;
}

@keyframes lk-toast-in-right {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
